import React, { useEffect } from 'react';
import Logo from '../../components/Layout/Logo';
import './PrivacyPolicyPage.css';

// Bump this whenever the policy content actually changes - it should reflect
// the date of the last real edit, not the date a visitor happens to load the page.
const LAST_UPDATED_DATE = 'August 24, 2026';

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    // Set current year (footer copyright - safe to keep dynamic)
    const yearElement = document.getElementById('currentYear');
    if (yearElement) {
      yearElement.textContent = new Date().getFullYear().toString();
    }
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-container">
        <header className="privacy-header">
          <div className="privacy-logo">
            <Logo height="4rem" />
          </div>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: {LAST_UPDATED_DATE}</p>
        </header>

        <section>
          <p>
            This Privacy Policy describes how Arctic Spas ("we," "our," or "us") collects, uses, and protects 
            your personal information when you use the Arctic Spas Dealer mobile application (the "App"). By using the App,
            you agree to the collection and use of information in accordance with this policy.
          </p>
          <div className="highlight">
            <p>
              <strong>Important:</strong> The App is completely free to download and use. We do not charge any fees, 
              require any payments, or offer any in-app purchases. The App is provided at no cost to all users.
            </p>
          </div>
        </section>

        <section>
          <h2>1. Information We Collect</h2>
          
          <h3>1.1 Personal Information</h3>
          <p>We may collect the following types of personal information:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, and account credentials</li>
            <li><strong>Spa Information:</strong> Spa model, serial number, purchase date, warranty status, and spa configuration settings</li>
            <li><strong>Bluetooth Device Information:</strong> Bluetooth device identifiers, connection history, device names, and signal strength data</li>
            <li><strong>Service Information:</strong> Work orders, warranty claims, and service history (if applicable)</li>
            <li><strong>App Settings:</strong> User preferences, spa control settings, and configuration data stored locally on your device</li>
          </ul>

          <h3>1.2 Device and Usage Information</h3>
          <ul>
            <li><strong>Device Information:</strong> Device model, operating system version, unique device identifiers</li>
            <li><strong>App Usage:</strong> Features used, frequency of use, and interaction patterns</li>
            <li><strong>Bluetooth Data:</strong> Bluetooth device identifiers, connection status, and signal strength (RSSI) for spa control and device discovery</li>
            <li><strong>Location Data (Indirect):</strong> When using Bluetooth to scan for nearby devices, your device's operating system may use location services. We do not directly collect or store your precise location, but Bluetooth scanning functionality may require location permissions on some devices. This is a system requirement for Bluetooth device discovery and is not used for tracking your location.</li>
          </ul>
          
          <h3>1.3 How We Collect Information</h3>
          <p>We collect information in the following ways:</p>
          <ul>
            <li><strong>Information You Provide:</strong> Account information, spa details, and preferences that you enter directly into the App</li>
            <li><strong>Automatically Collected:</strong> Device information, app usage data, and Bluetooth connection data collected automatically when you use the App</li>
            <li><strong>From Your Device:</strong> Information accessed through device permissions (Bluetooth, notifications) that you grant to the App</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          
          <p>We use the collected information for the following purposes:</p>
          
          <ul>
            <li><strong>App Functionality:</strong> To provide and maintain the App's core features, including Bluetooth-based spa control, device discovery, and connection management</li>
            <li><strong>Bluetooth Communication:</strong> To discover, connect to, and communicate with your Arctic Spas device for remote control (temperature, jets, lights, filters, etc.)</li>
            <li><strong>Device Setup:</strong> To assist with Bluetooth device pairing, WiFi configuration, and device verification</li>
            <li><strong>Customer Support:</strong> To respond to your inquiries, provide technical support, and assist with spa-related issues</li>
            <li><strong>Communications:</strong> To send you important updates about your account and app functionality via push notifications (if enabled)</li>
            <li><strong>Improvement:</strong> To analyze usage patterns and improve our services, features, and user experience</li>
            <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and other security threats</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Sharing and Disclosure</h2>
          
          <p>We may share your information in the following circumstances:</p>
          
          <ul>
            <li><strong>Service Providers:</strong> With third-party service providers who perform services on our behalf (e.g., cloud hosting, analytics, app infrastructure services). These service providers are contractually obligated to protect your information and use it only for the purposes we specify.</li>
            <li><strong>Service Technicians:</strong> With authorized service technicians to fulfill work orders and service requests (if applicable)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Your Consent:</strong> When you have explicitly consented to the sharing</li>
          </ul>

          <div className="highlight">
            <p><strong>We do not sell your personal information to third parties for marketing purposes.</strong></p>
          </div>
        </section>

        <section>
          <h2>4. Data Storage and Security</h2>
          
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and authorization systems</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls limiting who can access your information</li>
            <li>Secure cloud infrastructure with industry-standard security practices</li>
          </ul>
          
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive 
            to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>5. Your Rights and Choices</h2>
          
          <p>You have certain rights regarding your personal information:</p>
          
          <ul>
            <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
            <li><strong>Correction:</strong> You can update your account information directly through the App or by contacting us</li>
            <li><strong>Deletion:</strong> You can request deletion of your account and associated data by contacting us. We will process your request within 30 days, subject to legal obligations that may require us to retain certain information.</li>
            <li><strong>Opt-Out:</strong> You can opt out of marketing communications while still receiving essential service notifications (note: the App does not send marketing communications)</li>
            <li><strong>Data Portability:</strong> You can request a copy of your data in a machine-readable format</li>
          </ul>
          
          <p>
            To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
          </p>
        </section>

        <section>
          <h2>6. Permissions and Device Access</h2>
          
          <p>The App requests the following permissions:</p>
          
          <ul>
            <li><strong>Bluetooth:</strong> Required to discover and connect to your Arctic Spas device for remote control. This permission enables the app to:
              <ul>
                <li>Scan for nearby Bluetooth-enabled spa devices</li>
                <li>Establish secure connections with your spa</li>
                <li>Send and receive commands for temperature control, jets, lights, and other spa functions</li>
                <li>Configure WiFi settings on your spa device</li>
              </ul>
            </li>
            <li><strong>Location (if required by device):</strong> On some devices, Bluetooth scanning may require location permissions due to operating system requirements. We do not use this permission to track your location. It is only required by the device's operating system for Bluetooth functionality. You can deny location permission, but this may prevent Bluetooth device discovery on some devices.</li>
            <li><strong>Notifications:</strong> Optional permission to send you push notifications about app updates, spa status changes, and important account information. You can choose to enable or disable notifications at any time.</li>
          </ul>
          
          <p>
            <strong>Important:</strong> Bluetooth permission is essential for the app's core functionality. Without Bluetooth access, you will not be able to discover, connect to, or control your spa device. 
          </p>
          
          <p>
            You can revoke these permissions at any time through your device settings. Note that revoking Bluetooth permissions 
            will prevent the app from connecting to your spa device.
          </p>
        </section>

        <section>
          <h2>7. Children's Privacy</h2>
          
          <p>
            The App is not intended for children under the age of 13. We do not knowingly collect personal information from 
            children under 13. If you become aware that a child has provided us with personal information, please contact us, 
            and we will take steps to delete such information.
          </p>
          <p>
            <strong>Note:</strong> The App is free to use and does not require any payment information, making it safe for 
            families to use without concerns about accidental purchases.
          </p>
        </section>

        <section>
          <h2>8. International Data Transfers</h2>
          
          <p>
            Your information may be transferred to and processed in countries other than your country of residence. These 
            countries may have data protection laws that differ from those in your country. By using the App, you consent 
            to the transfer of your information to these countries.
          </p>
          <p>
            We take appropriate safeguards to ensure that your personal information receives an adequate level of protection
            regardless of where it is processed. For Quebec residents, see Section 8.3 below for details on how we assess
            transfers of personal information outside Quebec.
          </p>
        </section>

        <section>
          <h2>8.1 Your Privacy Rights (GDPR - EU Users)</h2>
          
          <p>
            If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have additional rights 
            under the General Data Protection Regulation (GDPR):
          </p>
          <ul>
            <li><strong>Right to Access:</strong> You can request a copy of your personal data</li>
            <li><strong>Right to Rectification:</strong> You can request correction of inaccurate data</li>
            <li><strong>Right to Erasure:</strong> You can request deletion of your personal data ("right to be forgotten")</li>
            <li><strong>Right to Restrict Processing:</strong> You can request limitation of how we process your data</li>
            <li><strong>Right to Data Portability:</strong> You can request transfer of your data to another service</li>
            <li><strong>Right to Object:</strong> You can object to processing of your personal data</li>
            <li><strong>Right to Withdraw Consent:</strong> You can withdraw consent at any time where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information in the "Contact Us" section. We will respond 
            to your request within one month.
          </p>
        </section>

        <section>
          <h2>8.2 Your Privacy Rights (CCPA - California Users)</h2>
          
          <p>
            If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
          </p>
          <ul>
            <li><strong>Right to Know:</strong> You can request disclosure of what personal information we collect, use, and share</li>
            <li><strong>Right to Delete:</strong> You can request deletion of your personal information</li>
            <li><strong>Right to Opt-Out:</strong> You can opt-out of the sale of personal information (we do not sell personal information)</li>
            <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information in the "Contact Us" section. We will respond
            to your request within 45 days.
          </p>
        </section>

        <section>
          <h2>8.3 Your Privacy Rights (Quebec Residents - Law 25)</h2>

          <p>
            If you are a resident of Quebec, you have rights under Quebec's <em>Act respecting the protection of
            personal information in the private sector</em>, as amended by the <em>Act to modernize legislative
            provisions respecting the protection of personal information</em> ("Law 25").
          </p>

          <h3>8.3.1 Privacy Officer</h3>
          <p>
            Arctic Spas has designated a Privacy Officer responsible for overseeing our compliance with applicable
            privacy laws, including Law 25, and for responding to privacy-related questions, complaints, and
            requests. You can reach our Privacy Officer using the contact information in the "Contact Us" section
            below.
          </p>

          <h3>8.3.2 Consent</h3>
          <p>
            Where we rely on your consent to collect, use, or disclose personal information, that consent is
            requested in clear and simple language, separately from other information, for specific purposes, and
            in a manner that is manifest, free, and informed. We request separate, specific consent before
            collecting or using sensitive personal information. You may withdraw your consent at any time, subject
            to legal or contractual restrictions and reasonable notice, by contacting us using the information in
            the "Contact Us" section below.
          </p>

          <h3>8.3.3 Privacy Impact Assessments and Transfers Outside Quebec</h3>
          <p>
            Before transferring personal information outside Quebec (including to service providers or
            infrastructure located elsewhere in Canada or abroad, such as Google Analytics as described in Section
            9), we conduct a privacy impact assessment (PIA) appropriate to the sensitivity of the information. This
            assessment considers, among other things:
          </p>
          <ul>
            <li>The sensitivity of the personal information being transferred;</li>
            <li>The purposes for which it will be used;</li>
            <li>The protection measures, including contractual measures, that would apply to it in the destination
              jurisdiction; and</li>
            <li>The legal framework applicable in the destination jurisdiction, including whether it provides a
              degree of protection equivalent to that afforded under Quebec law.</li>
          </ul>
          <p>
            We proceed with a transfer only where this assessment concludes that the personal information will
            receive adequate protection, generally reinforced by contractual commitments requiring the recipient to
            maintain protections substantially equivalent to those required under Quebec law.
          </p>

          <h3>8.3.4 Additional Rights for Quebec Residents</h3>
          <p>In addition to the rights described in Section 5 above, Quebec residents may:</p>
          <ul>
            <li>Request the cessation of dissemination of their personal information, or the de-indexing of a
              hyperlink giving access to it, where the dissemination of that information causes serious injury to
              their reputation or privacy that is clearly greater than the public's interest in knowing the
              information or than the interest of freedom of expression;</li>
            <li>Request that any personal information disseminated that is inaccurate, incomplete, or ambiguous, or
              that is not authorized by law to be collected, be re-indexed or, if that is insufficient, de-indexed;</li>
            <li>Be informed, on request, of any decision based exclusively on the automated processing of their
              personal information, of the personal information used in making that decision, of the reasons and
              principal factors that led to it, and of their right to have that information corrected; and</li>
            <li>File a complaint with the Commission d'accès à l'information (CAI) du Québec if they believe we have
              not complied with our obligations under Law 25.</li>
          </ul>
          <p>
            To exercise these rights, please contact our Privacy Officer using the information in the "Contact Us"
            section below. We will respond within the timelines required by applicable law.
          </p>
        </section>

        <section>
          <h2>9. Data Collection and Third-Party Services</h2>

          <p>The App may use the following types of third-party services:</p>
          <ul>
            <li><strong>Cloud Infrastructure:</strong> For hosting and storing app data and user accounts</li>
            <li>
              <strong>Google Analytics:</strong> We use Google Analytics to help us understand how the App is used
              (e.g., features accessed, session length, and general usage patterns) so we can improve the App.
              Google Analytics collects device and usage information on our behalf and transmits it to Google LLC
              for processing, which may take place on servers located outside Quebec and Canada, including in the
              United States. This processing is governed by Google's own privacy policy, available at{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                https://policies.google.com/privacy
              </a>
              . Where available on your device, you can limit or opt out of Google Analytics data collection using
              tools such as the{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                Google Analytics Opt-out Browser Add-on
              </a>{' '}
              or your device's privacy settings.
            </li>
            <li><strong>Push Notification Services:</strong> To deliver notifications to your device (if you enable notifications)</li>
          </ul>
          <p>
            These third-party services are bound by their own privacy policies and are contractually required to protect your
            information. We do not share your personal information with third parties for their own marketing purposes.
          </p>
        </section>

        <section>
          <h2>10. Data Retention</h2>

          <p>
            We retain your personal information for as long as necessary to provide you with our services, comply with legal
            obligations, resolve disputes, and enforce our agreements. When you delete your account, we will delete or
            anonymize your personal information, except where we are required to retain it for legal or legitimate business
            purposes.
          </p>
          <p>
            We have adopted internal governance rules for the retention and destruction of personal information, which we
            review periodically. When personal information is no longer necessary for the purposes for which it was
            collected, we securely destroy or anonymize it, subject to any longer retention period required by law. See
            Section 8.3 below for additional information that applies to Quebec residents under Law 25.
          </p>
        </section>

        <section>
          <h2>11. Data Breach Notification</h2>

          <p>
            We maintain an incident response process to help us detect, evaluate, and respond to security incidents
            involving personal information under our control.
          </p>
          <p>If we become aware of a confidentiality incident (a data breach) involving personal information under our control, we will:</p>
          <ul>
            <li>Take reasonable steps to reduce the risk of injury and to prevent further incidents;</li>
            <li>
              Where the incident presents a risk of serious injury, notify affected individuals and, as required by
              applicable law, the Commission d'accès à l'information (CAI) du Québec, the Office of the Privacy
              Commissioner of Canada, and/or other applicable regulators, as soon as reasonably possible; and
            </li>
            <li>Maintain a record of confidentiality incidents, as required by applicable law.</li>
          </ul>
          <p>
            Where notification to affected individuals is required, we will describe, to the extent known, the personal
            information involved, the circumstances of the incident, the steps we have taken or plan to take to reduce
            the risk of injury, and steps you can take to reduce your own risk (for example, changing your account
            password).
          </p>
          <p>
            If you suspect a data breach or have concerns about the security of your personal information, please
            contact our Privacy Officer using the information in the "Contact Us" section below.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the 
            new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy 
            Policy periodically for any changes.
          </p>
        </section>

        <section>
          <h2>13. App Pricing and Purchases</h2>
          
          <div className="highlight">
            <p>
              <strong>The App is completely free.</strong> We do not charge any fees for downloading, installing, or using 
              the App. There are no in-app purchases, subscriptions, premium features, or any other payment requirements. 
              All features and functionality of the App are available to all users at no cost.
            </p>
            <p>
              We do not collect, store, or process any payment information, credit card details, or billing information 
              because the App does not involve any financial transactions.
            </p>
          </div>
        </section>

        <section>
          <h2>14. Contact Us</h2>

          <div className="contact-info">
            <p>If you have any questions about this Privacy Policy, our data practices, or the App, or if you wish to exercise your privacy rights (including under Quebec's Law 25), please contact our Privacy Officer:</p>
            <p>
              <strong>Arctic Spas &mdash; Attn: Privacy Officer</strong><br />
              Email: <a href="mailto:app.support@arcticspas.com">app.support@arcticspas.com</a><br />
            </p>
            <p>
              <strong>Response Time:</strong> We will respond to your inquiries and privacy requests within 30 days (or 45 days for CCPA requests).
              For urgent matters, or to report a suspected data breach, please indicate "URGENT" or "DATA BREACH" in your subject line.
            </p>
          </div>
        </section>

        <footer className="privacy-footer">
          <p>&copy; <span id="currentYear"></span> Arctic Spas. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

